/*
 * Copyright © 2018, Rezo Zero
 *
 * @file WaveformManager.js
 * @author Adrien Scholaert <adrien@rezo-zero.com>
 */

import AManager from '../abstracts/AManager'
import Waveform from './Waveform'
import $ from 'jquery'
import { $window } from '../utils/environment'
import * as Utils from '../utils/Utils'
import MobileModal from '../common/MobileModal'

export default class WaveformManager extends AManager {
    constructor (sounds) {
        super(sounds)

        /**
         * @type {Array<Waveform>}
         */
        this.objects = []
        this.soundUrlText = `/assets/sounds/${sounds[0]}`
        this.soundUrlPhotos = `/assets/sounds/${sounds[1]}`
        this.audioEnable = false

        /**
         * @type {jQuery|HTMLElement}
         */
        this.$audioBtn = $('#audio-btn')

        // Bind methods
        this.onSetVolume = this.onSetVolume.bind(this)
        this.onAudioBtnClick = this.onAudioBtnClick.bind(this)
    }

    preload (p5) {
        let waveformText = new Waveform(this.soundUrlText, 'text', true)
        let waveformPhotos = new Waveform(this.soundUrlPhotos, 'photos', false)

        this.objects.push(waveformText)
        this.objects.push(waveformPhotos)

        for (let object of this.objects) {
            object.preload(p5)
        }

        // Init events
        this.$audioBtn.on('click', this.onAudioBtnClick)
        $window.on('setvolume', this.onSetVolume)
    }

    setup (p5) {
        for (let object of this.objects) {
            object.setup(p5)
        }

        if (!Utils.isMobile()) {
            this.launchActiveObject()
        } else {
            this.mobileModal = new MobileModal()
            this.mobileModal.on('launch', () => {
                p5.getAudioContext().resume()
                this.launchActiveObject()
            })
            this.mobileModal.init()
        }

        this.enableAudio()
    }

    launchActiveObject () {
        const activeObject = this.getActiveObject()

        if (activeObject) {
            activeObject.play()
        }
    }

    destroy () {
        this.$audioBtn.off('click', this.onAudioBtnClick)
        $window.off('setvolume', this.onSetVolume)
        this.disableAudio()

        if (this.mobileModal) {
            this.mobileModal.hide()
            this.mobileModal = null
        }
    }

    onSetVolume () {
        this.enableAudio()
    }

    onAudioBtnClick () {
        this.toggleAudio()
    }

    toggleAudio () {
        if (this.audioEnable) {
            this.disableAudio()
        } else {
            this.enableAudio()
        }
    }

    enableAudio () {
        this.$audioBtn.addClass('audio-enable')
        this.audioEnable = true

        for (let object of this.objects) {
            object.setVolume(1)
        }
    }

    disableAudio () {
        this.$audioBtn.removeClass('audio-enable')
        this.audioEnable = false

        for (let object of this.objects) {
            object.setVolume(0)
        }
    }

    draw (p5, blockAutoScroll) {
        for (let object of this.objects) {
            object.draw(p5, blockAutoScroll)
        }
    }

    setPercent (percent) {
        const activeObject = this.getActiveObject()

        if (!activeObject) return

        const percentNormalize = Math.min(Math.max(percent, 0), 100)
        const activePercentage = Math.min(Math.max(activeObject.percentage, 0), 100)
        const delta = percentNormalize - activePercentage

        // Mute sound
        if (this.audioEnable) {
            activeObject.setAmplitude(0)
            activeObject.setFrequence(Math.min(Math.round(Math.abs(delta) * 50), 150))
        }

        // Debounce for jump
        clearTimeout(this.pauseTimeout)
        this.pauseTimeout = window.setTimeout(() => {
            // Jump to position
            activeObject.jump(percentNormalize)

            // Fade in volume
            activeObject.fadeIn(0)

            // Reset sound effect
            activeObject.setFrequence(0)
        }, 0)
    }

    getActiveObject () {
        for (let object of this.objects) {
            if (object.active) {
                return object
            }
        }
    }

    changeSound () {
        for (let object of this.objects) {
            if (object.active) {
                object.disable()
            } else {
                object.enable()
            }
        }
    }

    onResize (p5) {
        for (let object of this.objects) {
            object.onResize(p5)
        }
    }
}



// WEBPACK FOOTER //
// ./webpack/js/waveform/WaveformManager.js